import { z } from "zod";
import { uuidLoose } from "./uuid.js";
/**
 * Order status (Zod-validated). Mirrors `packages/db/src/schema/sales.ts`
 * `orderStatusValues`. `out_for_delivery` is the P17 COD-only interstitial
 * between `placed` and `paid` — DB CHECK constraint enforces it only on
 * `payment_processor='cod'` rows.
 */
export const OrderStatusSchema = z.enum([
    "pending_payment",
    "placed",
    "out_for_delivery",
    "paid",
    "fulfilled",
    "cancelled",
    "partially_refunded",
    "refunded",
]);
/**
 * Cancellation reason (Zod-validated). P17 — mirrors
 * `packages/db/src/schema/sales.ts` `cancellationReasonValues`.
 * - `customer_request` — buyer cancels their own pending/placed order
 * - `cod_refused` — admin marks an `out_for_delivery` COD order refused
 * - `admin_action` — admin cancels (refund, abuse, manual ops)
 * - `stock_unavailable` — fulfilment couldn't be honoured
 * - `payment_expired` — V1.4.2: the pending-payment auto-expiry sweep
 *   cancelled an order whose Stripe PaymentIntent never settled in time
 *
 * DB CHECK enforces NOT NULL when `status='cancelled'`, NULL otherwise.
 */
export const CancellationReasonSchema = z.enum([
    "customer_request",
    "cod_refused",
    "admin_action",
    "stock_unavailable",
    "payment_expired",
]);
// ---------------------------------------------------------------------------
// Admin write-side request bodies (P08-T6+).
// ---------------------------------------------------------------------------
/**
 * Body for `POST /v1/admin/orders/:id/fulfil` (P08-T6).
 *
 * Both fields optional — V1.0 supports a tracking-less fulfilment (e.g.
 * counter handover) and a notes-less fulfilment (no admin annotation). Caps
 * mirror `RejectCustomerBody.reason` (500-char ceiling) — long-form context
 * belongs on a future order-comments surface, not the fulfilment payload.
 *
 * Wire keys are snake_case per spec §141 (`{tracking_number?, notes?}`).
 * The orderEvents.payload jsonb stores the same snake_case shape so the
 * audit-readable jsonb mirrors the wire body verbatim.
 */
export const FulfilOrderBody = z.object({
    tracking_number: z.string().min(1).max(120).optional(),
    notes: z.string().min(1).max(500).optional(),
});
/**
 * Body for `POST /v1/admin/orders/:id/cancel` (P08-T8).
 *
 * Single-field body: `{reason}`. snake_case is moot here (single key) but
 * spec §141 governs future fields — if a `cancel_code` enum or similar is
 * added in V1.5, snake_case wins (consistent with `RejectCustomerBody` +
 * `FulfilOrderBody`). The 500-char ceiling mirrors `RejectCustomerBody`
 * and `FulfilOrderBody.notes` — long-form context belongs on a future
 * order-comments surface, not the cancel payload.
 *
 * The reason is stored verbatim on:
 *   - `refunds.reason` (the freeform admin reason text — same value as the
 *     POST /v1/admin/refunds path).
 *   - `order_events.payload.reason` (jsonb camelCase per the `payload`
 *     storage convention — see RefundsService.settleFromWebhook docstring).
 *   - The `order.cancel` audit row's `afterJson.reason` (snake_case wire
 *     spelling on the audit DTO).
 */
export const CancelOrderBody = z.object({
    reason: z.string().min(1).max(500),
});
/**
 * Response of `POST /v1/storefront/orders/:id/payment-intent` (order pay page).
 * Mirrors InvoicePaymentIntentResponse: the storefront mounts a Stripe Payment
 * Element from `clientSecret`. Used by the quote-accept (immediate-pay) flow,
 * whose order lands `placed` with a PI and must be completed on the pay page.
 */
export const OrderPaymentIntentResponse = z.object({
    clientSecret: z.string(),
    paymentIntentId: z.string(),
});
/**
 * Body for `POST /v1/admin/orders` (P08-T1).
 *
 * Allows admin staff to create an order directly, specifying company location,
 * line items with optional price overrides, and settlement method (COD or paid).
 * Stripe link integration is deferred to a later phase.
 */
export const AdminCreateOrderBody = z.object({
    company_location_id: uuidLoose,
    line_items: z.array(z.object({
        variant_id: uuidLoose,
        quantity: z.coerce.number().int().positive(),
        unit_price_override_minor: z.coerce.number().int().min(0).optional(),
    })).min(1).max(200),
    settlement: z.object({ method: z.enum(["cod", "paid"]) }),
});
